import React, {
  useState,
  useEffect,
  FormEvent,
  MouseEvent,
  useMemo
} from 'react';
import Fuse from 'fuse.js';
import styled from 'styled-components';
import { useHistory, useLocation, useParams } from 'react-router';
import CustomerService from '~/App/shared/components/CustomerService';
import {
  Caption,
  Headline,
  mq,
  TinyTitleBold,
  TextField,
  theme,
  Title,
  ArrowRightIcon,
  SearchIcon,
  Spinner,
  Stack,
  Spacer,
  resetButtonStyle
} from '@kvdbil/components';
import { log } from '~/helpers/bugsnagHelper';
import { getSchemaFromFaq } from './Schema';
import { Helmet } from 'react-helmet';
import { translateRoute } from '~/App/shared/localization';
import { TranslateFunction, useTranslation } from '~/Locale';
import Link from '../../Link';
import FAQCategory from './FAQCategory';
import { useFAQPageCmsData } from '../hooks';
import StructuredText from '~/App/components/DatoCMS/StructuredText';
import Head from '../../Head';
import { getBaseCanonicalUrl } from '~/helpers/baseCanonicalUrl';
import { FaqQuestion } from '../cmsDataHandler';
import { extractTextStructuredTextObject } from '~/App/components/DatoCMS/StructuredText/utils';

const HelpHeader = styled.div`
  color: ${({ theme }) => theme.colors.text.light};
  width: 100%;
  background-color: ${theme.colors.primary.main};
  padding: 1rem;
  padding-top: 0;
`;

const HelpBody = styled.div`
  background: ${({ theme }) => theme.colors.background.light};
  padding: 2rem 1rem;
`;

const HelpResult = styled.div`
  padding-block: 0.5rem;
`;

const HelpNoResult = styled.div`
  padding: 3em 0.5em;
  text-align: center;
`;

const HelpArticle = styled.div`
  padding-block: 0.5rem;
`;

const HelpArticleHeading = styled(Title)`
  padding-block: 0.5rem;
  color: ${({ theme }) => theme.colors.grayscaleToned.dark3};
`;

const HelpCustomerService = styled.div`
  padding-block: 0.5rem;
`;

const HelpLoading = styled.div`
  padding: 3em 0.5em;
  text-align: center;
`;

const HelpBreadcrumbList = styled.ul`
  display: flex;
  align-items: center;
`;

const HelpBreadcrumbItem = styled.li`
  position: relative;
  display: inline-flex;
  align-items: center;

  &:nth-child(1n + 2) {
    margin-left: 0.5rem;
    margin-right: 0.5rem;
  }
`;

const HelpBreadcrumbText = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontBaseBoldWeight};
  color: inherit;
  word-break: break-word;
`;

const HelpBreadcrumbLink = styled.button`
  ${resetButtonStyle}
`;

const HelpBreadcrumbIcon = styled.span`
  color: ${({ theme }) => theme.colors.grayscaleToned.light1};
  font-size: 0.5rem;
  margin-right: 0.5rem;
`;

const HelpSearch = styled.div`
  background: ${({ theme }) => theme.colors.background.light};
  width: 100%;
`;

const HelpLinkListItem = styled.li`
  position: relative;
  border-bottom: 1px dotted ${({ theme }) => theme.colors.grayscaleToned.light2};
  padding: 0.5rem 0;
  cursor: pointer;
`;

const HelpLinkListLink = styled(Link)`
  padding-right: 1em;
  display: inline-block;
  vertical-align: top;
  width: calc(100% - 2em);
  text-align: left;
`;

const HelpLinkListArrowIcon = styled.span`
  color: ${({ theme }) => theme.colors.info.main};
  display: block;
  position: absolute;
  z-index: 1;
  right: 0;
  top: 40%;
  font-size: 0.75rem;
`;

const HelpHeaderContent = styled(Stack)`
  gap: 1rem;
`;

const DesktopWrapper = styled.div`
  ${mq('laptop')} {
    max-width: 1024px;
    margin: 0 auto;
  }
`;

const fuzzySearchOptions = {
  threshold: 0.4,
  location: 0,
  distance: 100,
  maxPatternLength: 32,
  minMatchCharLength: 3,
  keys: ['question']
};

export const createFaqURL = (t: TranslateFunction, slug: string) => {
  return `/${t('ROUTES.HELP')}${slug}`;
};

const prepareTextForSEO = (text: string, length: number) => {
  return text?.length > length ? text?.slice(0, length - 3) + '...' : text;
};

// This component handles sub views because it's meant to be a reusable component, for example in CMS as a block
export const FAQ = () => {
  const history = useHistory();
  const params = useParams<{ question: string }>();
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const { data: cmsData } = useFAQPageCmsData();

  const { question } = params;
  const questions = useMemo(() => cmsData?.questions, [cmsData?.questions]);
  const questionUrl = createFaqURL(t, '/' + question);
  const matchingQuestion = cmsData?.questionsBySlug?.['/' + question];
  const [activeQuestion, setActiveQuestion] =
    useState<Partial<FaqQuestion>>(matchingQuestion); // Set active question server side

  const [isLoading, setIsLoading] = useState(false);
  const [view, setView] = useState(matchingQuestion ? 'article' : 'start');

  const [searchResponse, setSearchResponse] = useState<FaqQuestion[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchBreadcrumb, setSearchBreadCrumb] = useState('');
  const [justChangedView, setJustChangedView] = useState(false);

  useEffect(() => {
    if (
      createFaqURL(t, activeQuestion?.slug ?? '') !== questionUrl &&
      question &&
      !justChangedView
    ) {
      if (!questions?.length) {
        return;
      }

      if (!matchingQuestion) {
        log(
          new Error(`No matching question found for: ${activeQuestion?.slug}`),
          { severity: 'info' }
        );
        return;
      }

      setView('article');
      setActiveQuestion(matchingQuestion);
    }
  }, [
    question,
    questionUrl,
    activeQuestion,
    questions,
    matchingQuestion,
    view,
    justChangedView,
    t
  ]);

  const handleArticleClick = (question: FaqQuestion) => {
    setJustChangedView(false);

    setView('article');
    setActiveQuestion(question);
  };

  const handleStartClick = () => {
    setView('start');
    setJustChangedView(true);
    history.push(translateRoute(t, '/ROUTES.HELP'));
  };

  const handleSearchSubmit = (
    event: FormEvent<HTMLFormElement> | MouseEvent<SVGSVGElement>
  ) => {
    event.preventDefault();

    if (!searchQuery) {
      return;
    }

    setIsLoading(true);

    const fuse = new Fuse(questions, fuzzySearchOptions);
    const response: FaqQuestion[] = fuse.search(searchQuery);

    setIsLoading(false);
    setJustChangedView(true);
    setView('searchResult');
    setSearchResponse(response);
    setSearchBreadCrumb(searchQuery);
  };

  const schema = useMemo(
    () => getSchemaFromFaq(cmsData?.questions),
    [cmsData?.questions]
  );

  const seoTitle =
    prepareTextForSEO(activeQuestion?.question ?? '', 60) ||
    t('PAGE_TITLES.HELP');

  const seoDescription = useMemo(
    () =>
      prepareTextForSEO(
        extractTextStructuredTextObject(activeQuestion?.answer?.value),
        160
      ),
    [activeQuestion?.answer?.value]
  );

  return (
    <>
      {!activeQuestion && (
        <Helmet>
          <script type={schema.type}>{schema.text}</script>
        </Helmet>
      )}

      <Head
        title={seoTitle}
        meta={[
          { name: 'og:title', content: seoTitle },
          {
            name: 'description',
            content: seoDescription
          },
          {
            name: 'og:description',
            content: seoDescription
          }
        ]}
        link={[{ rel: 'canonical', href: getBaseCanonicalUrl(pathname) }]}
      />

      <HelpHeader>
        <DesktopWrapper>
          <HelpHeaderContent>
            <Headline as={activeQuestion ? 'p' : 'h1'}>
              {cmsData?.title ?? t('Frequently asked questions')}
            </Headline>

            <TinyTitleBold>
              {cmsData?.subtitle ??
                t(
                  'Here we have collected answers to frequently asked questions'
                )}
            </TinyTitleBold>
          </HelpHeaderContent>
        </DesktopWrapper>
      </HelpHeader>
      <HelpBody>
        <DesktopWrapper>
          <HelpSearch>
            <div>
              <form onSubmit={handleSearchSubmit} noValidate>
                <TextField
                  data-testid="search-input"
                  placeholder={t('Search') + '...'}
                  onChange={event => setSearchQuery(event.target.value)}
                  suffix={<SearchIcon onClick={handleSearchSubmit} />}
                />
              </form>
            </div>
          </HelpSearch>

          <Spacer height={1} />

          <div>
            {view === 'start' && !isLoading ? (
              <div>
                {cmsData?.categories.map((category, index) => (
                  <FAQCategory
                    key={index}
                    category={category}
                    onArticleClick={handleArticleClick}
                  />
                ))}
              </div>
            ) : null}

            {(view === 'searchResult' || view === 'article') && (
              <div>
                <HelpBreadcrumbList>
                  <HelpBreadcrumbItem>
                    <HelpBreadcrumbLink onClick={handleStartClick}>
                      {t('Start')}
                    </HelpBreadcrumbLink>
                  </HelpBreadcrumbItem>

                  {view === 'article' && (
                    <HelpBreadcrumbItem>
                      <HelpBreadcrumbIcon>
                        <ArrowRightIcon />
                      </HelpBreadcrumbIcon>
                      <HelpBreadcrumbText data-testid="breadcrumb">
                        {activeQuestion?.question}
                      </HelpBreadcrumbText>
                    </HelpBreadcrumbItem>
                  )}

                  {view === 'searchResult' && (
                    <HelpBreadcrumbItem>
                      {searchResponse.length > 0 ? (
                        <>
                          <HelpBreadcrumbIcon>
                            <ArrowRightIcon />
                          </HelpBreadcrumbIcon>
                          <HelpBreadcrumbText>
                            {`${t('Searching term')} "${searchBreadcrumb}"`}
                          </HelpBreadcrumbText>
                        </>
                      ) : (
                        <HelpBreadcrumbText>
                          {t('No results')}
                        </HelpBreadcrumbText>
                      )}
                    </HelpBreadcrumbItem>
                  )}
                </HelpBreadcrumbList>
              </div>
            )}

            {isLoading && (
              <HelpLoading>
                <Spinner color="neutral" speed="fast" />
              </HelpLoading>
            )}

            {view === 'searchResult' &&
              Boolean(searchResponse.length) &&
              !isLoading && (
                <HelpResult>
                  <Caption>{t('Help articles that match')}</Caption>
                  <ul>
                    {searchResponse?.map((item, key) => (
                      <HelpLinkListItem key={key}>
                        <HelpLinkListLink
                          to={createFaqURL(t, item?.slug ?? '')}
                          onClick={() => handleArticleClick(item)}
                        >
                          {item.question}
                        </HelpLinkListLink>
                        <HelpLinkListArrowIcon>
                          <ArrowRightIcon />
                        </HelpLinkListArrowIcon>
                      </HelpLinkListItem>
                    ))}
                  </ul>
                </HelpResult>
              )}

            {view === 'searchResult' &&
              searchResponse.length <= 0 &&
              !isLoading && (
                <HelpNoResult>
                  <Title>{t('No matching pages or Help articles')}</Title>
                </HelpNoResult>
              )}

            {view === 'article' && activeQuestion && (
              <HelpArticle>
                <HelpArticleHeading as="h1">
                  {activeQuestion.question}
                </HelpArticleHeading>
                <StructuredText data={activeQuestion?.answer} />
              </HelpArticle>
            )}

            {(view === 'start' || (view === 'article' && !isLoading)) && (
              <HelpCustomerService>
                <Spacer height={1} />
                <CustomerService />
              </HelpCustomerService>
            )}
          </div>
        </DesktopWrapper>
      </HelpBody>
    </>
  );
};

export default FAQ;
